<template>
  <v-container class="reports-page" fluid>
    <header class="reports-header">
      <div>
        <h1>Reports</h1>
        <p>Find and open the information you need.</p>
      </div>
      <v-chip class="report-count" color="primary" outlined>
        {{ filteredReports.length }} {{ filteredReports.length === 1 ? "report" : "reports" }}
      </v-chip>
    </header>

    <v-text-field
      v-model="search"
      class="reports-search"
      outlined
      prepend-inner-icon="mdi-magnify"
      background-color="#F8FAF8"
      placeholder="Search reports by name or category"
      aria-label="Search reports"
      single-line
      hide-details
      clearable
    />

    <div v-if="filteredGroups.length" class="report-groups">
      <section v-for="group in filteredGroups" :key="group.name" class="report-group">
        <div class="group-heading">
          <v-icon color="primary" size="22">{{ group.icon }}</v-icon>
          <h2>{{ group.name }}</h2>
          <span>{{ group.items.length }}</span>
        </div>

        <v-row dense>
          <v-col v-for="report in group.items" :key="report.title" cols="12" sm="6" lg="4">
            <v-card
              class="report-card"
              outlined
              ripple
              tabindex="0"
              role="button"
              @click="selectReport(report)"
              @keydown.enter="selectReport(report)"
              @keydown.space.prevent="selectReport(report)"
            >
              <div class="report-icon">
                <v-icon color="primary" size="25">{{ report.icon }}</v-icon>
              </div>
              <div class="report-copy">
                <h3>{{ report.title }}</h3>
                <p>{{ report.description }}</p>
              </div>
              <v-icon class="report-arrow" color="grey lighten-1" size="21">mdi-chevron-right</v-icon>
            </v-card>
          </v-col>
        </v-row>
      </section>
    </div>

    <div v-else class="empty-state">
      <v-icon size="48" color="grey lighten-1">mdi-file-search-outline</v-icon>
      <h2>No reports found</h2>
      <p>Try another report name or category.</p>
      <v-btn text color="primary" @click="search = ''">Clear search</v-btn>
    </div>

    <DateSelector @submit="openDatedReport" />
  </v-container>
</template>

<script>
import DateSelector from "@/modules/Report/components/DateSelector";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ReportHome",
  components: { DateSelector },
  data() {
    return {
      search: "",
      groups: [
        {
          name: "Students",
          icon: "mdi-account-school-outline",
          items: [
            { title: "Student List", description: "View a complete list of students.", icon: "mdi-account-group-outline", path: "/reportView/students/list" },
            { title: "Students With Open Invoice", description: "Find students with outstanding invoices.", icon: "mdi-account-cash-outline", datePath: "students/openInvoice" },
          ],
        },
        {
          name: "Attendance",
          icon: "mdi-calendar-check-outline",
          items: [
            { title: "Attendance By Class", description: "Review attendance grouped by class.", icon: "mdi-google-classroom", path: "/reportView/attendance/class" },
            { title: "Attendance By Student", description: "Review attendance for an individual student.", icon: "mdi-account-check-outline", path: "/reportView/attendance/student" },
          ],
        },
        {
          name: "Invoices",
          icon: "mdi-receipt-text-outline",
          items: [
            { title: "Invoice By Product", description: "Analyze invoices for each product.", icon: "mdi-package-variant-closed", datePath: "invoice/byProduct" },
            { title: "Invoice By Campaign", description: "Analyze invoices for each campaign.", icon: "mdi-bullhorn-outline", datePath: "invoice/byCampaign" },
            { title: "Invoice By Donation Category", description: "Review invoices by donation category.", icon: "mdi-hand-heart-outline", datePath: "invoice/byDonationCategory" },
            { title: "Open Invoices (Online)", description: "View online invoices that remain open.", icon: "mdi-receipt-text-clock-outline", path: "/reportView/invoice/openInvoice" },
            { title: "Student Fee Discrepancy", description: "Identify differences in student fees.", icon: "mdi-alert-circle-outline", path: "/reportView/invoice/subscriptionDiscrepancy" },
            { title: "Unsettled Invoices", description: "View invoices awaiting settlement.", icon: "mdi-cash-clock", path: "/reportView/transaction/list" },
          ],
        },
        {
          name: "Departments",
          icon: "mdi-domain",
          items: [
            { title: "Pending Balance By Department", description: "Review outstanding balances by department.", icon: "mdi-scale-balance", path: "/reportView/department/all" },
          ],
        },
        {
          name: "School Collections",
          icon: "mdi-school-outline",
          items: [
            { title: "Collection By Department", description: "Compare invoices and money collected for each class or department.", icon: "mdi-chart-bar-stacked", datePath: "payment/department/summary" },
          ],
        },
        {
          name: "Donations",
          icon: "mdi-hand-heart-outline",
          items: [
            { title: "Donation Collection By Month", description: "Track donation collections over time.", icon: "mdi-chart-bar", datePath: "payment/donation" },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters("reportView", ["getReportName"]),
    normalizedSearch() {
      return (this.search || "").trim().toLowerCase();
    },
    filteredGroups() {
      if (!this.normalizedSearch) return this.groups;
      return this.groups
        .map(group => ({
          ...group,
          items: group.items.filter(report =>
            `${group.name} ${report.title} ${report.description}`.toLowerCase().includes(this.normalizedSearch)
          ),
        }))
        .filter(group => group.items.length);
    },
    filteredReports() {
      return this.filteredGroups.reduce((reports, group) => reports.concat(group.items), []);
    },
  },
  methods: {
    ...mapActions("reportView", ["setDateSelector", "setReportName"]),
    selectReport(report) {
      if (report.datePath) {
        this.setReportName(report.datePath);
        this.setDateSelector(true);
        return;
      }
      const routeData = this.$router.resolve({ path: report.path });
      window.open(routeData.href, "_blank");
    },
    openDatedReport(value) {
      const routeData = this.$router.resolve({
        path: `/reportView/${this.getReportName}`,
        query: { start: value.start, end: value.end },
      });
      window.open(routeData.href, "_blank");
    },
  },
};
</script>

<style scoped>
.reports-page { max-width: 1280px; padding: 28px 32px 48px; }
.reports-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 22px; }
.reports-header h1 { margin: 0; color: #172033; font-size: 29px; line-height: 1.2; letter-spacing: -.025em; }
.reports-header p { margin: 6px 0 0; color: #728095; font-size: 13px; }
.report-count { font-weight: 600; }
.reports-search { max-width: 640px; margin-bottom: 34px; }
.reports-search ::v-deep .v-input__slot { min-height: 48px; border-radius: 10px; }
.report-groups { display: grid; gap: 32px; }
.group-heading { display: flex; align-items: center; gap: 9px; margin-bottom: 12px; }
.group-heading h2 { margin: 0; color: #273348; font-size: 16px; font-weight: 600; }
.group-heading span { min-width: 22px; padding: 2px 7px; border-radius: 10px; color: #738095; background: #eef2f6; font-size: 11px; text-align: center; }
.report-card { display: flex; align-items: center; min-height: 104px; height: 100%; padding: 18px; border-color: #e4e9ef !important; border-radius: 12px; cursor: pointer; transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }
.report-card:hover, .report-card:focus { border-color: var(--v-primary-base) !important; box-shadow: 0 7px 22px rgba(32, 50, 76, .09); outline: none; transform: translateY(-2px); }
.report-icon { display: flex; flex: 0 0 46px; align-items: center; justify-content: center; width: 46px; height: 46px; margin-right: 14px; border-radius: 11px; background: rgba(25, 118, 210, .08); }
.report-copy { min-width: 0; flex: 1; }
.report-copy h3 { margin: 0 0 5px; color: #263247; font-size: 14px; font-weight: 600; line-height: 1.3; }
.report-copy p { margin: 0; color: #7b8798; font-size: 12px; line-height: 1.45; }
.report-arrow { margin-left: 10px; }
.empty-state { padding: 70px 20px; border: 1px dashed #d9e0e8; border-radius: 12px; text-align: center; }
.empty-state h2 { margin: 14px 0 5px; color: #344054; font-size: 17px; }
.empty-state p { margin: 0 0 8px; color: #8490a1; font-size: 13px; }
@media (max-width: 600px) {
  .reports-page { padding: 22px 16px 40px; }
  .reports-header { align-items: flex-start; }
  .reports-header h1 { font-size: 25px; }
  .report-count { display: none; }
  .reports-search { margin-bottom: 28px; }
  .report-groups { gap: 26px; }
}
</style>
